import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import NewAnalysisCard from "./components/NewAnalysisCard";
import { isAuthed, getIdToken } from "../../lib/cognito";

const STATUS_CHIPS = [
  { label: "System", value: "Online", color: "#10B981" },
  { label: "AI Model", value: "Ready", color: "#3B82F6" },
  { label: "Response", value: "<1s", color: "#06B6D4" },
];

const Dashboard = () => {
  const [displayName, setDisplayName] = useState("User");
  const navigate = useNavigate();

  useEffect(() => {
    async function loadUser() {
      try {
        const authed = await isAuthed();
        if (!authed) return navigate("/login-screen");

        const token = await getIdToken();
        if (!token) return navigate("/login-screen");

        const jwtPayload = JSON.parse(atob(token.split(".")[1]));

        const name =
          jwtPayload.name ||
          jwtPayload.given_name ||
          jwtPayload.preferred_username ||
          (jwtPayload.email ? jwtPayload.email.split("@")[0] : "User");

        setDisplayName(name);
      } catch {
        navigate("/login-screen");
      }
    }

    loadUser();
  }, [navigate]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Dashboard - Graft Counter</title>
      </Helmet>

      <div
        className="relative min-h-screen w-full overflow-hidden"
        style={{ background: "#060d1c" }}
      >
        {/* CSS grid pattern — no image file */}
        <div
          className="fixed inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(59,130,246,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.05) 1px, transparent 1px)",
            backgroundSize: "52px 52px",
          }}
        />

        {/* Ambient glows */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div
            className="absolute"
            style={{
              top: "-160px",
              left: "-80px",
              width: "700px",
              height: "700px",
              background:
                "radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 68%)",
            }}
          />
          <div
            className="absolute"
            style={{
              top: "30%",
              right: "-100px",
              width: "500px",
              height: "500px",
              background:
                "radial-gradient(circle, rgba(6,182,212,0.08) 0%, transparent 70%)",
            }}
          />
          <div
            className="absolute"
            style={{
              bottom: "-60px",
              left: "35%",
              width: "600px",
              height: "400px",
              background:
                "radial-gradient(circle, rgba(139,92,246,0.06) 0%, transparent 70%)",
            }}
          />
        </div>

        {/* CONTENT */}
        <div className="relative z-10 flex flex-col min-h-screen">
          <Header />

          {/* CENTER */}
          <div className="flex flex-col items-center justify-center flex-1 px-6 py-10">

            {/* Status chips */}
            <div className="w-full max-w-4xl flex items-center gap-3 flex-wrap mb-10">
              {STATUS_CHIPS.map(({ label, value, color }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full"
                  style={{
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full animate-pulse"
                    style={{ background: color }}
                  />
                  <span
                    className="text-xs"
                    style={{ color: "rgba(255,255,255,0.38)" }}
                  >
                    {label}
                  </span>
                  <span
                    className="text-xs font-semibold"
                    style={{ color }}
                  >
                    {value}
                  </span>
                </div>
              ))}
            </div>

            {/* Welcome */}
            <div className="w-full max-w-4xl mb-10">
              <p
                className="text-xs mb-2 tracking-[0.18em] uppercase"
                style={{
                  color: "rgba(255,255,255,0.22)",
                  fontFamily: "'JetBrains Mono', monospace",
                }}
              >
                {new Date().toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                })}
              </p>
              <h1
                className="text-4xl font-light tracking-tight"
                style={{ color: "#E2E8F0" }}
              >
                Welcome back,{" "}
                <span
                  style={{
                    background:
                      "linear-gradient(135deg, #60A5FA 0%, #22D3EE 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {displayName}
                </span>
              </h1>
              <p
                className="text-sm mt-2"
                style={{ color: "rgba(255,255,255,0.3)" }}
              >
                Your AI-powered hair transplant graft analysis platform is ready.
              </p>
            </div>

            {/* NewAnalysisCard */}
            <div className="w-full max-w-4xl">
              <NewAnalysisCard />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
